/**
 * Inicio de la Tienda. Las tareas solo aparecen si hay algo pendiente.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { esCount } from '@/lib/es-count';
import { formatDate, formatEurFromCents, formatNumber } from '@/lib/format-es';
import { STATUS_TONE_VARIANT } from '@/lib/store-status';
import { cn } from '@/lib/utils';
import {
    buildHomeTasks,
    salesInPeriod,
    salesWithActiveSuppliers,
    summarizeOriginSales,
    type SalesPeriod,
} from './storeHomeModel';
import { useMerchStockAlerts } from '../products/useMerchStock';
import { useStoreHome } from './useStoreHome';
import { StoreWebPublishBanner } from './StoreWebPublishBanner';

function Kpi({ label, value, hint }: { label: string; value: string; hint: string }) {
    return (
        <article className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className="mt-2 text-2xl font-bold tabular-nums text-foreground">{value}</p>
            <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
        </article>
    );
}

export function StoreHomePage() {
    const home = useStoreHome();
    const stockAlerts = useMerchStockAlerts();
    const [period, setPeriod] = useState<SalesPeriod>('all');
    const summary = home.data;

    const tasks = useMemo(() => {
        if (!summary) return [];
        return buildHomeTasks({
            ...summary,
            lowStockCount: stockAlerts.data?.lowOrOut ?? 0,
            lotPendingCount: stockAlerts.data?.lotPending ?? 0,
            now: new Date(),
            formatDate: (iso) => formatDate(iso),
        });
    }, [summary, stockAlerts.data]);

    const originRows = useMemo(() => {
        if (!summary) return [];
        return salesWithActiveSuppliers(summarizeOriginSales(summary.saleLines, new Date()), summary.suppliers);
    }, [summary]);
    const sales = useMemo(() => salesInPeriod(originRows, period), [originRows, period]);

    if (home.isLoading) {
        return (
            <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className="h-16 animate-pulse rounded-xl bg-muted" />
                ))}
            </div>
        );
    }

    if (home.isError || !summary) {
        return <p className="text-sm text-red-400">No se ha podido cargar el inicio de la tienda.</p>;
    }

    const kpis = summary.kpis;
    const marginMissing = kpis.goodsCostCents == null;
    const goodsAllCents = originRows.reduce((sum, row) => sum + row.allCents, 0);
    const shippingCents = period === 'month' ? summary.shippingMonthCents : summary.shippingAllCents;

    return (
        <div className="space-y-8">
            <PageHeader
                title="Inicio"
                description="Lo que pide atención hoy y cómo van las ventas por origen."
            />
            <StoreWebPublishBanner />

            <section className="space-y-3">
                <h2 className="text-sm font-semibold text-foreground">Para hacer hoy</h2>
                {tasks.length === 0 ? (
                    <p className="rounded-xl border border-border bg-card px-4 py-6 text-sm text-muted-foreground">
                        Todo al día
                    </p>
                ) : (
                    <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
                        {tasks.map((task) => (
                            <li key={task.id}>
                                <Link to={task.href} className="flex items-start gap-3 px-4 py-3 hover:bg-muted/40">
                                    <span className="w-12 shrink-0 text-lg font-bold tabular-nums text-foreground">
                                        {formatNumber(task.count)}
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-medium text-foreground">{task.title}</span>
                                        <span className="block text-xs text-muted-foreground">{task.detail}</span>
                                    </span>
                                    <StatusBadge
                                        status={task.tone}
                                        variant={STATUS_TONE_VARIANT[task.tone]}
                                        label={task.badge}
                                    />
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="text-sm font-semibold text-foreground">Ventas por origen</h2>
                        <p className="text-xs text-muted-foreground">Ventas de producto, sin envío.</p>
                    </div>
                    <div className="inline-flex rounded-lg border border-border p-0.5">
                        {([
                            ['month', 'Este mes'],
                            ['all', 'Histórico'],
                        ] as const).map(([id, label]) => (
                            <button
                                key={id}
                                type="button"
                                aria-pressed={period === id}
                                onClick={() => setPeriod(id)}
                                className={cn(
                                    'rounded-md px-3 py-1.5 text-sm',
                                    period === id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                                )}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
                {sales.length === 0 && shippingCents === 0 ? (
                    <p className="text-sm text-muted-foreground">Sin ventas en este periodo.</p>
                ) : (
                    <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
                        {sales.map((row) => (
                            <li key={row.key} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                                <span className="font-medium text-foreground">{row.label}</span>
                                <span className="text-right tabular-nums">
                                    <span className="block font-semibold">{formatEurFromCents(row.cents)}</span>
                                    <span className="text-xs text-muted-foreground">
                                        {esCount(row.orders, 'pedido')}
                                    </span>
                                </span>
                            </li>
                        ))}
                        <li className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                            <span className="text-muted-foreground">Envíos</span>
                            <span className="font-semibold tabular-nums">{formatEurFromCents(shippingCents)}</span>
                        </li>
                    </ul>
                )}
            </section>

            <section className="grid grid-cols-2 gap-3 xl:grid-cols-5">
                <Kpi
                    label="Pendientes de envío"
                    value={formatNumber(kpis.pendingShipment)}
                    hint={`${esCount(kpis.paid, 'pagado')} · ${esCount(kpis.processing, 'en preparación', 'en preparación')}`}
                />
                <Kpi
                    label="Enviados"
                    value={formatNumber(kpis.shipped)}
                    hint={esCount(kpis.delivered, 'entregado')}
                />
                <Kpi
                    label="Ingresos del mes"
                    value={formatEurFromCents(kpis.revenueMonthCents)}
                    hint={`Histórico ${formatEurFromCents(kpis.revenueAllTimeCents)}`}
                />
                <Kpi
                    label="Pedidos totales"
                    value={formatNumber(kpis.totalOrders)}
                    hint={`Ticket medio ${formatEurFromCents(kpis.avgTicketCents)}`}
                />
                <Kpi
                    label="Margen bruto"
                    value={marginMissing ? '—' : formatEurFromCents(goodsAllCents - (kpis.goodsCostCents ?? 0))}
                    hint={marginMissing ? 'sin costes cargados' : `Coste ${formatEurFromCents(kpis.goodsCostCents)}`}
                />
            </section>
        </div>
    );
}
